import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Empty } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function AutoAssignDialog({ audit, open, onOpenChange, onDone }) {
  const { tx } = useI18n();
  const [members, setMembers] = useState([]);
  const [ids, setIds] = useState([]);
  const [prev, setPrev] = useState(null);
  useEffect(() => {
    api.get(`/audits/${audit.id}/members`).then((r) => {
      const m = r.data.filter((x) => !x.revoked_at && x.user_status === "active" && ["consultant", "lead"].includes(x.role));
      setMembers(m); setIds(m.filter((x) => x.role === "consultant").map((x) => x.user_id));
    }).catch(() => {});
  }, [audit.id]);
  const preview = useCallback(() => {
    if (!ids.length) { setPrev(null); return; }
    api.post(`/audits/${audit.id}/interviews/auto-assign`, { consultant_ids: ids, dry_run: true }).then((r) => setPrev(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [audit.id, ids]);
  useEffect(() => { preview(); }, [preview]);
  const go = async () => {
    try {
      const { data } = await api.post(`/audits/${audit.id}/interviews/auto-assign`, { consultant_ids: ids });
      toast.success(`${data.to_assign} ${tx("fiche(s) réparties", "sheet(s) distributed")}`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const split = Object.fromEntries((prev?.split || []).map((s) => [s.consultant_id, s]));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auto-assign-dialog" className="w-[calc(100vw-2rem)] max-w-lg">
        <DialogHeader><DialogTitle className="text-[#153A63]">{tx("Répartir automatiquement", "Distribute automatically")}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{tx("Les fiches non attribuées sont réparties entre les auditeurs cochés, en tenant compte des entretiens qu'ils ont déjà en cours.", "Unassigned sheets are split between the ticked auditors, taking their current open interviews into account.")}</p>
        {members.length === 0 ? <Empty text={tx("Aucun auditeur sur la mission : ajoutez-en dans Effectifs & auditeurs", "No auditor on this mission: add one in Workforce & auditors")} /> : (
          <div className="overflow-x-auto"><table className="tbl text-sm" data-testid="auto-assign-table">
            <thead><tr><th /><th>{tx("Auditeur", "Auditor")}</th><th>{tx("Nouvelles fiches", "New sheets")}</th><th>{tx("Total en cours", "Total open")}</th></tr></thead>
            <tbody>{members.map((m) => (
              <tr key={m.user_id} data-testid={`auto-assign-row-${m.user_id}`}>
                <td><Checkbox data-testid={`auto-assign-check-${m.user_id}`} checked={ids.includes(m.user_id)} onCheckedChange={(v) => setIds(v ? [...ids, m.user_id] : ids.filter((x) => x !== m.user_id))} /></td>
                <td>{m.name}<div className="text-[11px] text-slate-400">{m.role === "lead" ? tx("Chef de mission", "Mission lead") : m.job_function || tx("Consultant", "Consultant")}</div></td>
                <td className="font-mono" data-testid={`auto-assign-new-${m.user_id}`}>{split[m.user_id] ? `+${split[m.user_id].new}` : "—"}</td>
                <td className="font-mono">{split[m.user_id]?.total_open ?? "—"}</td>
              </tr>))}</tbody>
          </table></div>
        )}
        <div className="text-sm" data-testid="auto-assign-count">{tx("Fiches à répartir", "Sheets to distribute")} : <b>{prev?.to_assign ?? 0}</b></div>
        <DialogFooter><Button data-testid="auto-assign-submit" disabled={!ids.length || !prev?.to_assign} onClick={go} className="bg-[#153A63]">{tx("Répartir", "Distribute")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
